import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import { StageConnector, StageEndMarker } from "../../../services/stage-connectors";
import { AttachedStageDestination, AttachedTrailStart, StageDestination, parsePoiItemId, stageEndDayForMarker } from "../../../services/stage-destinations";
import { showSnapTarget, hideSnapTarget } from "./markers";
import { resolveAttachTarget } from "./attach-target";
import { POIUtility } from "../../../../../utils/models/POI";
import mapboxgl from "mapbox-gl";
import type { FeatureCollection, Feature, Point, LineString } from 'geojson';

/**
 * Stage connectors — map rendering and drag interaction.
 *
 * A connector is a single draggable handle shown where one stage ends and
 * the next begins (see services/stage-connectors.ts for detection).
 * Dragging it moves both endpoints together; the re-routing itself lives in
 * drag-preview.ts (updateConnectorDragPreview / finalizeConnectorDrag).
 */

const CONNECTOR_HANDLE_SOURCE = 'stage-connector-handles';
const CONNECTOR_HANDLE_LAYER = 'stage-connector-handle-layer';
// Attached: the item's own glyph drawn on top of the yellow disc
const CONNECTOR_ICON_LAYER = 'stage-connector-icon-layer';
const CONNECTOR_LABEL_LAYER = 'stage-connector-label-layer';
const CONNECTOR_TETHER_SOURCE = 'stage-connector-tethers';
const CONNECTOR_TETHER_LAYER = 'stage-connector-tether-layer';

// Overall trail start/finish pins (same icons + sizing as HikeMap.vue)
const TRAIL_START_SOURCE = 'plan-trail-start-marker';
const TRAIL_START_LAYER = 'plan-trail-start-marker';
const TRAIL_END_SOURCE = 'plan-trail-end-marker';
const TRAIL_END_LAYER = 'plan-trail-end-marker';

// Attached trail start: ring-around-the-icon, DECORATION ONLY — deliberately
// separate from CONNECTOR_HANDLE_LAYER (which is draggable and hardcoded to
// move a route's END marker on drop). The underlying marker is index 0 of
// the route, not an end, so it must keep dragging through the normal marker
// system rather than that connector-handle drag path. See
// updateTrailEndpointMarkersOnMap.
const TRAIL_START_RING_SOURCE = 'plan-trail-start-ring';
const TRAIL_START_RING_LAYER = 'plan-trail-start-ring-layer';
const TRAIL_START_RING_ICON_LAYER = 'plan-trail-start-ring-icon-layer';

// "Start here" anchor — shown on the previous route's end point while the
// active route is still empty; clicking it starts the new route exactly there
export const START_ANCHOR_SOURCE = 'route-start-anchor';
export const START_ANCHOR_LAYER = 'route-start-anchor-layer';

// Match the consolidated control-point rendering exactly (markers.ts uses
// icon-size 0.25, center anchor) so a point keeps the same on-screen
// position and size when it swaps between pin / control point / badge.
const ENDPOINT_ICON_SIZE = 0.25;

// In-memory copy of the handle features so drags can update positions
// without rebuilding from state on every mousemove.
let handleData: FeatureCollection | null = null;

// Listener refs for cleanup (mirrors the consolidated-marker pattern)
let listenerRefs: Function[] = [];
let anchorListenerRefs: Function[] = [];

function emptyCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * Open an attached item's own detail dialog.
 *
 * An attached stage end renders as a ring ON TOP of the item, so the map's POI
 * click never sees it: isClickOnPOIHelper only inspects the topmost rendered
 * feature, which is this layer's handle. Without this, attaching a POI to a day
 * silently makes that POI unclickable. Called from the plain-click (no drag)
 * paths, so dragging the ring still moves the boundary as before.
 *
 * Returns false when there's nothing to open, leaving the caller's existing
 * click behaviour intact — only POIs have a dialog reachable from here;
 * waypoints and highlights keep their current handling.
 */
function openAttachedItemDialog(destination: StageDestination | null | undefined): boolean {
  if (!destination || destination.itemType !== 'poi') return false;

  const parsed = parsePoiItemId(destination.itemId);
  if (!parsed) return false;

  const showPoi = (window as any).showPoi;
  if (typeof showPoi !== 'function') return false;

  // Seeded the same way the map's own POI click seeds it, so the dialog shows
  // the name and position immediately rather than blank while it fetches.
  showPoi(parsed.categoryType, parsed.poiId, {
    id: parsed.poiId,
    type: destination.poiType || parsed.categoryType,
    name: destination.name || null,
    lonlat: [destination.coordinates[0], destination.coordinates[1]],
  });
  return true;
}

// Every main-line stage end renders a numbered marker. Connected stages get
// a draggable connector handle (connectorId set); "floating" stage ends —
// not within the threshold of the next stage — get the same marker but
// display-only (empty connectorId). Stage ends attached to a destination
// (POI/waypoint/highlight) render as a RING around the item's icon instead
// of the filled badge; the final day's end only gets a marker at all while
// attached (its ring wraps the destination in place of the finish pin) —
// dragging that ring correctly moves the route's END marker, since that's
// genuinely what it is. The trail's overall START does NOT reuse this
// system (see updateTrailEndpointMarkersOnMap / TRAIL_START_RING_LAYER
// instead): this layer's drag handling is hardcoded to END-marker semantics
// (route.markers[length-1]), which would silently corrupt the route if
// applied to the START marker (index 0) instead.
// The item's glyph (same icon images the hike-highlight layer uses) drawn on
// the yellow disc when attached. Falls back to the generic icon on any
// unknown type so we never render a blank disc.
function iconImageForType(poiType: string): string {
  try {
    return POIUtility.fromString(poiType).hikeHighlightIcon();
  } catch (_) {
    return 'highlight_icon_custom';
  }
}

export function buildHandleData(
  stageEnds: StageEndMarker[],
  attachedDestinations: AttachedStageDestination[]
): FeatureCollection {
  const attachedByRouteKey = new Map(attachedDestinations.map(entry => [entry.routeKey, entry]));

  const features: Feature<Point>[] = stageEnds.map(stageEnd => {
    const attachedEntry = attachedByRouteKey.get(stageEnd.routeKey);
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [stageEnd.position[0], stageEnd.position[1]]
      },
      properties: {
        connectorId: stageEnd.connector?.id ?? '',
        prevRouteKey: stageEnd.connector?.prevRouteKey ?? '',
        nextRouteKey: stageEnd.connector?.nextRouteKey ?? '',
        coincident: stageEnd.connector?.coincident ?? false,
        // Route whose end this marker sits on — used to auto-activate it
        // when the marker isn't a draggable connector
        routeKey: stageEnd.routeKey,
        label: String(stageEnd.stageNumber),
        attached: !!attachedEntry,
        iconImage: attachedEntry ? iconImageForType(attachedEntry.destination.poiType) : ''
      }
    };
  });

  // Attached FINAL day: no stage-end marker exists (its end is the trail
  // finish), so add a ring feature of its own. It behaves like a floating
  // badge — dragging it moves the route's end marker (detaching it).
  const coveredRouteKeys = new Set(stageEnds.map(stageEnd => stageEnd.routeKey));
  for (const entry of attachedDestinations) {
    if (coveredRouteKeys.has(entry.routeKey)) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [entry.destination.coordinates[0], entry.destination.coordinates[1]]
      },
      properties: {
        connectorId: '',
        prevRouteKey: '',
        nextRouteKey: '',
        coincident: false,
        routeKey: entry.routeKey,
        label: String(entry.stageNumber),
        attached: true,
        iconImage: iconImageForType(entry.destination.poiType)
      }
    });
  }

  return { type: 'FeatureCollection', features };
}

export function buildTetherData(stageEnds: StageEndMarker[]): FeatureCollection {
  // Only draw a tether when a connector exists and its endpoints haven't
  // converged yet — it shows which two points the handle is bridging.
  const features: Feature<LineString>[] = stageEnds
    .filter(stageEnd => stageEnd.connector && !stageEnd.connector.coincident)
    .map(stageEnd => ({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [stageEnd.connector!.prevEnd, stageEnd.connector!.nextStart]
      },
      properties: { connectorId: stageEnd.connector!.id }
    }));
  return { type: 'FeatureCollection', features };
}

function removeConnectorLayers(map: mapboxgl.Map) {
  safeRemoveLayer(CONNECTOR_LABEL_LAYER);
  safeRemoveLayer(CONNECTOR_ICON_LAYER);
  safeRemoveLayer(CONNECTOR_HANDLE_LAYER);
  safeRemoveLayer(CONNECTOR_TETHER_LAYER);
  [CONNECTOR_HANDLE_SOURCE, CONNECTOR_TETHER_SOURCE].forEach(sourceId => {
    if (map.getSource(sourceId)) {
      try { map.removeSource(sourceId); } catch (_) {}
    }
  });
}

function cleanupConnectorListeners(map: mapboxgl.Map) {
  listenerRefs.forEach(ref => {
    map.off('mouseenter', CONNECTOR_HANDLE_LAYER, ref as any);
    map.off('mouseleave', CONNECTOR_HANDLE_LAYER, ref as any);
    map.off('mousemove', CONNECTOR_HANDLE_LAYER, ref as any);
    map.off('mousedown', CONNECTOR_HANDLE_LAYER, ref as any);
    map.off('touchstart', CONNECTOR_HANDLE_LAYER, ref as any);
  });
  listenerRefs = [];
}

export const stageConnectorActions = {
  /**
   * (Re)render connector handles + tethers from current route state.
   * Called from updateRouteAndMarkers so it refreshes with every geometry
   * change (drags, adds, removals, merges, reorders, undo/redo).
   */
  async updateStageConnectorsOnMap({ state, getters, commit, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    const stageEnds: StageEndMarker[] = getters.stageEndMarkers;

    // Attachment is defined by coincidence — prune destination entries whose
    // end marker no longer sits on the item (dragged away, day deleted).
    // This runs after every geometry change, so a detach-by-drag settles here.
    const attachedByDay: { [dayNumber: number]: AttachedStageDestination } = getters.attachedStageDestinations;
    Object.keys(state.stageDestinations).forEach(dayKey => {
      if (!attachedByDay[Number(dayKey)]) {
        commit('removeStageDestination', Number(dayKey));
      }
    });
    const attachedDestinations = Object.values(attachedByDay);

    if (!stageEnds.length && !attachedDestinations.length) {
      cleanupConnectorListeners(map);
      removeConnectorLayers(map);
      handleData = null;
      return;
    }

    handleData = buildHandleData(stageEnds, attachedDestinations);
    const tetherData = buildTetherData(stageEnds);

    const handleSource = map.getSource(CONNECTOR_HANDLE_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    const tetherSource = map.getSource(CONNECTOR_TETHER_SOURCE) as mapboxgl.GeoJSONSource | undefined;

    if (handleSource && tetherSource && map.getLayer(CONNECTOR_HANDLE_LAYER)) {
      try {
        handleSource.setData(handleData as any);
        tetherSource.setData(tetherData as any);
      } catch (_) {}
      if (listenerRefs.length > 0) return;
      // Listeners missing (e.g. after style change) — fall through to attach
    } else {
      // Tether below the handle
      layerRegistry.addSource(CONNECTOR_TETHER_SOURCE, {
        type: 'geojson',
        data: tetherData
      }, true);
      const tetherLayer: mapboxgl.LineLayer = {
        id: CONNECTOR_TETHER_LAYER,
        type: 'line',
        source: CONNECTOR_TETHER_SOURCE,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#fddf00',
          'line-width': 2,
          'line-dasharray': [1, 2],
          'line-opacity': 0.7
        }
      };
      layerRegistry.addLayer(tetherLayer, MapLayerCategory.Annotations, { z: 12 });

      layerRegistry.addSource(CONNECTOR_HANDLE_SOURCE, {
        type: 'geojson',
        data: handleData
      }, true);
      // Above the consolidated markers (z 13) — the connector supersedes the
      // endpoint markers it covers. Attached stage ends render as a solid
      // yellow disc with the item's glyph on top and the stage number lifted
      // above it (matching the hike-planner planner).
      const isAttached: any = ['boolean', ['get', 'attached'], false];

      // The handle disc: a filled yellow numbered badge when free-standing; a
      // larger solid yellow disc that sits BEHIND the item's glyph when
      // attached to a POI/waypoint/highlight.
      const handleLayer: mapboxgl.CircleLayer = {
        id: CONNECTOR_HANDLE_LAYER,
        type: 'circle',
        source: CONNECTOR_HANDLE_SOURCE,
        paint: {
          'circle-radius': ['case', isAttached, 15, 11] as any,
          'circle-color': '#fddf00',
          'circle-stroke-color': '#17181d',
          'circle-stroke-width': 2.5,
          'circle-pitch-alignment': 'map'
        }
      };
      // Stage markers sit in the Top category ABOVE the POI/waypoint/highlight
      // icons (waypoints render in Top; highlights in Annotations) so the
      // numbered disc/glyph is never covered by the item it marks.
      layerRegistry.addLayer(handleLayer, MapLayerCategory.Top, { z: 20 });

      // Attached-only: the item's glyph drawn ON the yellow disc, so the disc
      // reads as a yellow border behind the marker.
      const iconLayer: mapboxgl.SymbolLayer = {
        id: CONNECTOR_ICON_LAYER,
        type: 'symbol',
        source: CONNECTOR_HANDLE_SOURCE,
        filter: ['==', ['get', 'attached'], true] as any,
        layout: {
          'icon-image': ['get', 'iconImage'] as any,
          'icon-size': 0.26,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center'
        }
      };
      layerRegistry.addLayer(iconLayer, MapLayerCategory.Top, { z: 21 });

      // Stage number label. Free-standing: centred on the yellow disc.
      // Attached: lifted above the glyph as white text with a dark halo (like
      // the hike-planner planner) — no separate badge shape.
      const labelLayer: mapboxgl.SymbolLayer = {
        id: CONNECTOR_LABEL_LAYER,
        type: 'symbol',
        source: CONNECTOR_HANDLE_SOURCE,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': ['case', isAttached, 13, 12] as any,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-anchor': 'center',
          // Lift above the glyph when attached; centred on the disc otherwise
          'text-offset': ['case', isAttached, ['literal', [0, -2.1]], ['literal', [0, 0]]] as any,
          // Track the map plane exactly like the circle beneath
          // (circle-pitch-alignment: map), so pitch/rotation can't split them
          'text-pitch-alignment': 'map',
          'text-rotation-alignment': 'map'
        },
        paint: {
          // Attached: white number with a dark halo, floating above the glyph.
          // Free-standing: dark number on the yellow disc.
          'text-color': ['case', isAttached, '#ffffff', '#17181d'] as any,
          'text-halo-color': '#17181d',
          'text-halo-width': ['case', isAttached, 1.6, 0] as any
        }
      };
      layerRegistry.addLayer(labelLayer, MapLayerCategory.Top, { z: 22 });
    }

    if (!map.getLayer(CONNECTOR_HANDLE_LAYER)) return;

    // (Re)attach interaction listeners
    cleanupConnectorListeners(map);

    // Cursor must be set on the canvas ELEMENT — the global hover handler
    // (actions-refactored) re-asserts 'crosshair' there on every mousemove,
    // so container-level cursors never win.
    const canvas = map.getCanvas();
    let dragConnectorId: string | null = null;
    let lastDragLngLat: mapboxgl.LngLat | null = null;
    // Floating (unconnected) stage badge being dragged — it's the drag
    // handle for its route's END marker
    let floatingDragKey: string | null = null;
    let floatingMoved = false;
    let lastFloatLngLat: mapboxgl.LngLat | null = null;
    // Attach-to-destination: the day whose stage end is being dragged, and
    // the item currently under the cursor (plus-cursor snap target)
    let dragAttachDay: number | null = null;
    let dragAttachTarget: StageDestination | null = null;
    // Engaged item kept across moves so the vicinity snap has hysteresis
    let engagedAttachTarget: StageDestination | null = null;

    // Live drag preview of the attached look: flag the dragged handle feature
    // so it renders as the ring-around-item + number-above badge while snapped
    // to an object, exactly as it will look once dropped.
    const setDragFeatureAttached = (attached: boolean) => {
      if (!handleData) return;
      const feature = dragConnectorId
        ? handleData.features.find(f => f.properties?.connectorId === dragConnectorId)
        : (floatingDragKey
            ? handleData.features.find(f => !f.properties?.connectorId && f.properties?.routeKey === floatingDragKey)
            : null);
      if (!feature || !feature.properties) return;
      const iconImage = attached && dragAttachTarget ? iconImageForType(dragAttachTarget.poiType) : '';
      if (feature.properties.attached === attached && feature.properties.iconImage === iconImage) return;
      feature.properties.attached = attached;
      feature.properties.iconImage = iconImage;
      const src = map.getSource(CONNECTOR_HANDLE_SOURCE) as mapboxgl.GeoJSONSource;
      if (src) { try { src.setData(handleData as any); } catch (_) {} }
    };

    const moveHandleTo = (lng: number, lat: number) => {
      if (!handleData || !dragConnectorId) return;
      const feature = handleData.features.find(
        f => f.properties?.connectorId === dragConnectorId
      );
      if (feature && feature.geometry.type === 'Point') {
        (feature.geometry as Point).coordinates = [lng, lat];
        const src = map.getSource(CONNECTOR_HANDLE_SOURCE) as mapboxgl.GeoJSONSource;
        if (src) {
          try { src.setData(handleData as any); } catch (_) {}
        }
      }
    };

    async function onMove(e: mapboxgl.MapMouseEvent) {
      if (!dragConnectorId) return;

      // Resolve the FINAL position before writing it anywhere — moving the
      // handle to the cursor and then immediately again to an attach target
      // (two separate setData calls, so two separate paints) is what made
      // attaching to a nearby POI flicker between the two positions.
      let targetLngLat = e.lngLat;
      dragAttachTarget = null;
      if (dragAttachDay != null) {
        const target = resolveAttachTarget(map, e.point, engagedAttachTarget);
        if (target) {
          dragAttachTarget = target;
          engagedAttachTarget = target;
          targetLngLat = new mapboxgl.LngLat(target.coordinates[0], target.coordinates[1]);
        } else {
          engagedAttachTarget = null;
        }
      }

      lastDragLngLat = targetLngLat;
      moveHandleTo(targetLngLat.lng, targetLngLat.lat);

      if (dragAttachTarget) {
        // Preview the attached look (ring around item + number above)
        hideSnapTarget(map);
        setDragFeatureAttached(true);
        canvas.style.cursor = 'copy';
        return;
      }

      setDragFeatureAttached(false);
      hideSnapTarget(map);
      canvas.style.cursor = 'grabbing';
      await (dispatch as any)('updateConnectorDragPreview', [targetLngLat.lng, targetLngLat.lat]);
    }

    async function onUp() {
      map.off('mousemove', onMove);
      map.off('touchmove', onMove as any);
      document.removeEventListener('keydown', onKeyDown);

      if (!dragConnectorId) return;

      // Attach drop: the attach action moves both endpoints exactly onto
      // the item, records the destination and refreshes decorations —
      // the network-snapped route preview is discarded.
      if (dragAttachTarget && dragAttachDay != null) {
        const dayNumber = dragAttachDay;
        const destination = dragAttachTarget;
        const connectorId = dragConnectorId;
        dragConnectorId = null;
        lastDragLngLat = null;
        dragAttachDay = null;
        dragAttachTarget = null;
        engagedAttachTarget = null;
        hideSnapTarget(map);
        (dispatch as any)('cancelDragPreview');

        // Locked mode keeps the day's SLICE on the frozen trail and detours
        // out to the item — see the equivalent branch in markers.ts. Both
        // adjacent routes rebuild, since a connector is the junction between
        // them.
        if ((state as any).routeLocked && connectorId) {
          const [prevRouteKey, nextRouteKey] = connectorId.split('::');
          // Falls through to the plain attach below when there's no frozen
          // trail to gate against.
          if (await (dispatch as any)('applyLockedBoundaryDrag', {
            boundaryDay: dayNumber,
            prevRouteKey,
            nextRouteKey,
            targetCoord: [destination.coordinates[0], destination.coordinates[1]],
            destination,
          })) {
            await (dispatch as any)('updateRouteAndMarkers');
            canvas.style.cursor = 'grab';
            return;
          }
        }

        await (dispatch as any)('attachStageDestination', { dayNumber, destination });
        canvas.style.cursor = 'grab';
        return;
      }

      // Captured before the reset below — the plain-click branch needs to know
      // which day's attachment this handle was covering.
      const clickedDay = dragAttachDay;
      dragConnectorId = null;
      dragAttachDay = null;
      engagedAttachTarget = null;

      // Plain click (no movement) — nothing to apply, so don't re-route.
      // Without this, finalize would fall back to the handle's original
      // position and needlessly rebuild both adjacent segments.
      if (!lastDragLngLat) {
        (dispatch as any)('cancelDragPreview');
        // This ring sits on top of the item, so a tap here is the user's only
        // way to reach the item's own dialog.
        openAttachedItemDialog(clickedDay != null ? state.stageDestinations[clickedDay] : null);
        await (dispatch as any)('updateStageConnectorsOnMap');
        canvas.style.cursor = 'grab';
        return;
      }

      const fallbackPosition = [lastDragLngLat.lng, lastDragLngLat.lat] as [number, number];
      lastDragLngLat = null;

      const applied = await (dispatch as any)('finalizeConnectorDrag', { fallbackPosition });
      if (!applied) {
        // Nothing moved — put the handle back where the routes say it is
        await (dispatch as any)('updateStageConnectorsOnMap');
      }
      canvas.style.cursor = 'grab';
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      map.off('mousemove', onMove);
      map.off('touchmove', onMove as any);
      map.off('mousemove', floatingMove);
      map.off('touchmove', floatingMove as any);
      document.removeEventListener('keydown', onKeyDown);
      dragConnectorId = null;
      lastDragLngLat = null;
      floatingDragKey = null;
      floatingMoved = false;
      lastFloatLngLat = null;
      dragAttachDay = null;
      dragAttachTarget = null;
      engagedAttachTarget = null;
      hideSnapTarget(map);

      (dispatch as any)('cancelDragPreview');
      (dispatch as any)('updateStageConnectorsOnMap');
      canvas.style.cursor = 'grab';
    }

    const moveFloatingBadgeTo = (routeKey: string, lng: number, lat: number) => {
      if (!handleData) return;
      const feature = handleData.features.find(
        f => !f.properties?.connectorId && f.properties?.routeKey === routeKey
      );
      if (feature && feature.geometry.type === 'Point') {
        (feature.geometry as Point).coordinates = [lng, lat];
        const src = map.getSource(CONNECTOR_HANDLE_SOURCE) as mapboxgl.GeoJSONSource;
        if (src) {
          try { src.setData(handleData as any); } catch (_) {}
        }
      }
    };

    async function floatingMove(e: mapboxgl.MapMouseEvent) {
      if (!floatingDragKey) return;
      floatingMoved = true;

      // Resolve the FINAL position before writing it anywhere — moving the
      // badge to the cursor and then immediately again to an attach target
      // (two separate setData calls, so two separate paints) is what made
      // attaching to a nearby POI flicker between the two positions.
      let targetLngLat = e.lngLat;
      dragAttachTarget = null;
      if (dragAttachDay != null) {
        const target = resolveAttachTarget(map, e.point, engagedAttachTarget);
        if (target) {
          dragAttachTarget = target;
          engagedAttachTarget = target;
          targetLngLat = new mapboxgl.LngLat(target.coordinates[0], target.coordinates[1]);
        } else {
          engagedAttachTarget = null;
        }
      }

      lastFloatLngLat = targetLngLat;
      moveFloatingBadgeTo(floatingDragKey, targetLngLat.lng, targetLngLat.lat);

      if (dragAttachTarget) {
        // Preview the attached look (ring around item + number above)
        hideSnapTarget(map);
        setDragFeatureAttached(true);
        canvas.style.cursor = 'copy';
        return;
      }

      setDragFeatureAttached(false);
      hideSnapTarget(map);
      canvas.style.cursor = 'grabbing';
      // Locked mode never reroutes the trail — no reroute preview to show;
      // floatingUp resolves the boundary/detour once the drag ends instead.
      if ((state as any).isSnap && !(state as any).routeLocked) {
        await (dispatch as any)('updateDragPreview', [targetLngLat.lng, targetLngLat.lat]);
      }
    }

    async function floatingUp() {
      map.off('mousemove', floatingMove);
      map.off('touchmove', floatingMove as any);
      document.removeEventListener('keydown', onKeyDown);

      if (!floatingDragKey) return;
      const routeKey = floatingDragKey;
      floatingDragKey = null;

      // Plain click (no movement) — activate the route, same as before.
      //
      // EXCEPT: when the active route is a fresh, empty route waiting for a
      // start point, the "start here" anchor (updateStartAnchorOnMap) is
      // ALSO offered at this exact coordinate whenever this badge's route is
      // the closest preceding one — the two affordances stack on the same
      // spot (most visibly when this route's end is attached to a POI/
      // waypoint/highlight, so the badge is a big draggable ring, not an
      // invisible circle). Reactivating the clicked route here would silently
      // abandon the very route the user just created — start it here
      // instead, matching what tapping the anchor itself would do.
      if (!floatingMoved) {
        const clickedDay = dragAttachDay;
        dragAttachDay = null;
        dragAttachTarget = null;
        engagedAttachTarget = null;
        (dispatch as any)('cancelDragPreview');

        // The badge covers the item it's attached to, so a tap has to reach the
        // item's dialog rather than only (re)activating the route beneath it.
        if (openAttachedItemDialog(clickedDay != null ? state.stageDestinations[clickedDay] : null)) {
          canvas.style.cursor = 'grab';
          return;
        }

        const activeRoute = (state as any).activeRouteKey ? state.routes[(state as any).activeRouteKey] : null;
        if (activeRoute && activeRoute.markers.length === 0 && routeKey !== (state as any).activeRouteKey) {
          const previousEntry = Object.entries(state.routes)
            .filter(([key, route]) => key !== (state as any).activeRouteKey &&
              route.markers.length > 0 &&
              route.position < activeRoute.position)
            .sort((a, b) => b[1].position - a[1].position)[0];
          if (previousEntry && previousEntry[0] === routeKey) {
            const endMarker = previousEntry[1].markers[previousEntry[1].markers.length - 1];
            const [lng, lat] = endMarker.markerGeometry.coordinates;
            await (dispatch as any)('addMarkerAtCoordinates', { lng, lat });
            canvas.style.cursor = 'grab';
            return;
          }
        }

        if ((state as any).activeRouteKey !== routeKey && state.routes[routeKey]) {
          commit('setActiveRoute', routeKey);
          await (dispatch as any)('addAllRoutesToMap');
        }
        canvas.style.cursor = 'grab';
        return;
      }

      // Attach drop — exact-position attach, no network-snapped preview
      if (dragAttachTarget && dragAttachDay != null) {
        const dayNumber = dragAttachDay;
        const destination = dragAttachTarget;
        dragAttachDay = null;
        dragAttachTarget = null;
        lastFloatLngLat = null;
        floatingMoved = false;
        engagedAttachTarget = null;
        hideSnapTarget(map);
        (dispatch as any)('cancelDragPreview');

        // Locked mode keeps the day's SLICE on the frozen trail and detours
        // out to the item — see the equivalent branch in markers.ts.
        if ((state as any).routeLocked) {
          const route = state.routes[routeKey];
          if (route && await (dispatch as any)('applyLockedBoundaryDrag', {
            boundaryDay: route.day ?? 1,
            prevRouteKey: routeKey,
            nextRouteKey: null,
            targetCoord: [destination.coordinates[0], destination.coordinates[1]],
            destination,
          })) {
            await (dispatch as any)('updateRouteAndMarkers');
            canvas.style.cursor = 'grab';
            return;
          }
        }

        await (dispatch as any)('attachStageDestination', { dayNumber, destination });
        canvas.style.cursor = 'grab';
        return;
      }

      dragAttachDay = null;
      dragAttachTarget = null;
      engagedAttachTarget = null;

      // Locked mode: the frozen master trail never reroutes. Reposition this
      // day's own end boundary along it instead (no connected next-day route
      // to also rebuild, since this stage end is floating/disconnected).
      if ((state as any).routeLocked && lastFloatLngLat) {
        const route = state.routes[routeKey];
        if (route) {
          const boundaryDay = route.day ?? 1;
          const targetCoord: [number, number] = [lastFloatLngLat.lng, lastFloatLngLat.lat];
          (dispatch as any)('cancelDragPreview');
          await (dispatch as any)('applyLockedBoundaryDrag', {
            boundaryDay, prevRouteKey: routeKey, nextRouteKey: null, targetCoord,
          });
        }
        lastFloatLngLat = null;
        floatingMoved = false;
        await (dispatch as any)('updateRouteAndMarkers');
        canvas.style.cursor = 'grab';
        return;
      }

      // Drag: move the route's END marker (single-route, no activation)
      let previewApplied = false;
      if ((state as any).isSnap && (state as any).dragPreview.isDragging) {
        previewApplied = await (dispatch as any)('finalizeDragPreview');
      }
      if (!previewApplied && lastFloatLngLat) {
        const route = state.routes[routeKey];
        if (route && route.markers.length > 0) {
          const marker = route.markers[route.markers.length - 1];
          const oldPosition = [...marker.markerGeometry.coordinates];
          marker.markerGeometry.coordinates = [lastFloatLngLat.lng, lastFloatLngLat.lat];
          await (dispatch as any)('markerMoved', { marker, oldPosition, routeKey });
        }
      }
      lastFloatLngLat = null;
      floatingMoved = false;

      // markerMoved refreshes decorations; make sure the badge lands even
      // when the fallback path was skipped
      await (dispatch as any)('updateStageConnectorsOnMap');
      canvas.style.cursor = 'grab';
    }

    // Referenced hikes CAN be re-routed via a connector, but only after the
    // same "changes affect all hikes using this route" confirmation that
    // gates regular marker drags on references (see markers.ts).
    function referenceGate(feature: any): boolean {
      const referencedRoutes = [
        state.routes[feature.properties?.prevRouteKey],
        state.routes[feature.properties?.nextRouteKey]
      ].filter(route => route && route.isReference);
      if (referencedRoutes.length === 0) return true;

      const blocked = typeof window.canEditReference === 'function' &&
        referencedRoutes.some(route => !window.canEditReference(route));
      if (!blocked) return true;

      // Show the warning modal; once confirmed the user can re-try the drag
      if (typeof window.checkReferenceEditWarning === 'function') {
        window.checkReferenceEditWarning(referencedRoutes[0]);
      }
      return false;
    }

    function startDrag(feature: any) {
      dragConnectorId = feature.properties?.connectorId ?? null;
      if (!dragConnectorId) return false;

      // The connector marks the END of the previous route's day — that's
      // the day an attach drop would set the destination for
      const prevRoute = state.routes[feature.properties?.prevRouteKey];
      dragAttachDay = prevRoute ? (prevRoute.day ?? 1) : null;
      dragAttachTarget = null;
      engagedAttachTarget = null;

      (dispatch as any)('initializeConnectorDrag', {
        prevRouteKey: feature.properties.prevRouteKey,
        nextRouteKey: feature.properties.nextRouteKey,
        position: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]]
      });
      document.addEventListener('keydown', onKeyDown);
      return true;
    }

    function mouseDown(e: mapboxgl.MapMouseEvent & { features?: any[] }) {
      // Left button only — right/middle clicks must not start a drag
      if (e.originalEvent && e.originalEvent.button !== 0) return;
      const feature = e.features && e.features[0];
      if (!feature) return;
      // Floating stage-end markers (no connector) are the drag handle for
      // their route's END marker: drag moves the endpoint (no activation);
      // a plain click activates the route.
      if (!feature.properties?.connectorId) {
        const routeKey = feature.properties?.routeKey;
        const route = routeKey ? state.routes[routeKey] : null;
        if (!route || route.markers.length === 0) return;

        // Moving the endpoint edits the route — reference warning applies
        const win = window as any;
        if (route.isReference &&
            typeof win.canEditReference === 'function' && !win.canEditReference(route)) {
          if (typeof win.checkReferenceEditWarning === 'function') {
            win.checkReferenceEditWarning(route);
          }
          return;
        }

        e.preventDefault();
        floatingDragKey = routeKey;
        floatingMoved = false;
        lastFloatLngLat = null;
        dragAttachDay = stageEndDayForMarker(state.routes, routeKey, route.markers.length - 1);
        dragAttachTarget = null;
        engagedAttachTarget = null;
        if ((state as any).isSnap) {
          (dispatch as any)('initializeDragPreview', {
            markerIndex: route.markers.length - 1,
            routeId: routeKey,
            position: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]]
          });
        }
        document.addEventListener('keydown', onKeyDown);
        map.on('mousemove', floatingMove);
        map.once('mouseup', floatingUp as any);
        return;
      }
      if (!referenceGate(feature)) return;
      e.preventDefault();
      if (!startDrag(feature)) return;
      map.on('mousemove', onMove);
      map.once('mouseup', onUp as any);
    }

    function touchStart(e: any) {
      if (!e.points || e.points.length !== 1) return;
      const feature = e.features && e.features[0];
      if (!feature) return;
      if (!feature.properties?.connectorId) return;
      if (!referenceGate(feature)) return;
      e.preventDefault();
      if (!startDrag(feature)) return;
      map.on('touchmove', onMove as any);
      map.once('touchend', onUp as any);
    }

    // The global hover handler resets the cursor every mousemove, so a
    // one-shot mouseenter isn't enough — re-assert it per-move while over a
    // draggable handle. (Layer listeners registered after the global one run
    // later in the same event, so this wins.)
    function hoverCursor(e: any) {
      if (dragConnectorId || floatingDragKey) return;
      // Both kinds of stage marker are draggable (connectors move the shared
      // point; floating badges move their route's end)
      canvas.style.cursor = 'grab';
    }

    function leave() {
      if (!dragConnectorId && !floatingDragKey) canvas.style.cursor = 'crosshair';
    }

    listenerRefs = [onMove, onUp, floatingMove, floatingUp, hoverCursor, leave, mouseDown, touchStart];
    map.on('mouseenter', CONNECTOR_HANDLE_LAYER, hoverCursor);
    map.on('mousemove', CONNECTOR_HANDLE_LAYER, hoverCursor);
    map.on('mouseleave', CONNECTOR_HANDLE_LAYER, leave);
    map.on('mousedown', CONNECTOR_HANDLE_LAYER, mouseDown as any);
    map.on('touchstart', CONNECTOR_HANDLE_LAYER, touchStart as any);
  },

  /**
   * Render the overall trail start pin (start of the FIRST main-line route)
   * and finish pin (end of the LAST main-line route) — the same markers our
   * read-only maps show. The pin is skipped for whichever of those routes is
   * currently active, since the active route renders its own draggable
   * start/end control points.
   *
   * Also renders the trail-start ring (see TRAIL_START_RING_LAYER) in place
   * of the plain start pin when attached to a destination, and prunes the
   * attachment once the start marker no longer sits on it (dragged away —
   * this then just re-renders as the standard start pin, same as detaching a
   * stage destination). This is DECORATION ONLY, unlike the equivalent
   * stage-end ring (buildHandleData): it must not reuse
   * CONNECTOR_HANDLE_LAYER's drag handling, which is hardcoded to move a
   * route's END marker and would corrupt the route if applied to the START
   * marker (index 0) instead. The marker itself keeps dragging through the
   * normal marker-drag path.
   */
  async updateTrailEndpointMarkersOnMap({ state, getters, commit }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    const upsert = (sourceId: string, layerId: string, coords: number[] | null, icon: string) => {
      const fc: FeatureCollection = {
        type: 'FeatureCollection',
        features: coords ? [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [coords[0], coords[1]] },
          properties: {}
        }] : []
      };
      const existing = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
      if (existing) {
        try { existing.setData(fc as any); } catch (_) {}
      } else if (coords) {
        layerRegistry.addSource(sourceId, { type: 'geojson', data: fc }, true);
      }
      if (coords && !map.getLayer(layerId)) {
        const layer: mapboxgl.SymbolLayer = {
          id: layerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'icon-image': icon,
            'icon-size': ENDPOINT_ICON_SIZE,
            // Center anchor — identical to the consolidated control points,
            // so the marker doesn't jump when the point changes role
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
          }
        };
        layerRegistry.addLayer(layer, MapLayerCategory.Annotations, { z: 12 });
      }
    };

    const upsertStartRing = (attached: AttachedTrailStart | null) => {
      const fc: FeatureCollection = {
        type: 'FeatureCollection',
        features: attached ? [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [attached.destination.coordinates[0], attached.destination.coordinates[1]]
          },
          properties: { iconImage: iconImageForType(attached.destination.poiType) }
        }] : []
      };
      const existing = map.getSource(TRAIL_START_RING_SOURCE) as mapboxgl.GeoJSONSource | undefined;
      if (existing) {
        try { existing.setData(fc as any); } catch (_) {}
      } else if (attached) {
        layerRegistry.addSource(TRAIL_START_RING_SOURCE, { type: 'geojson', data: fc }, true);
      }
      if (attached && !map.getLayer(TRAIL_START_RING_LAYER)) {
        const ringLayer: mapboxgl.CircleLayer = {
          id: TRAIL_START_RING_LAYER,
          type: 'circle',
          source: TRAIL_START_RING_SOURCE,
          paint: {
            'circle-radius': 15,
            'circle-color': '#fddf00',
            'circle-stroke-color': '#17181d',
            'circle-stroke-width': 2.5,
            'circle-pitch-alignment': 'map'
          }
        };
        layerRegistry.addLayer(ringLayer, MapLayerCategory.Top, { z: 20 });
      }
      if (attached && !map.getLayer(TRAIL_START_RING_ICON_LAYER)) {
        const iconLayer: mapboxgl.SymbolLayer = {
          id: TRAIL_START_RING_ICON_LAYER,
          type: 'symbol',
          source: TRAIL_START_RING_SOURCE,
          layout: {
            'icon-image': ['get', 'iconImage'] as any,
            'icon-size': 0.26,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center'
          }
        };
        layerRegistry.addLayer(iconLayer, MapLayerCategory.Top, { z: 21 });
      }
    };

    // Attachment is defined by coincidence — prune it once the start marker
    // no longer sits on the item (dragged away). Runs before anything else
    // reads it, so a detach-by-drag settles within this same refresh.
    const attachedTrailStart: AttachedTrailStart | null = getters.attachedTrailStart;
    if (state.trailStartDestination && !attachedTrailStart) {
      commit('removeTrailStartDestination');
    }
    upsertStartRing(attachedTrailStart);

    // Main-line routes only — offshoots don't define the trail's start/finish
    const mains = Object.entries(state.routes)
      .filter(([, route]) => (route.route_style ?? 'route') === 'route' && route.markers?.length > 0)
      .sort((a, b) => a[1].position - b[1].position);

    if (mains.length === 0) {
      upsert(TRAIL_START_SOURCE, TRAIL_START_LAYER, null, 'hike-control-point-start');
      upsert(TRAIL_END_SOURCE, TRAIL_END_LAYER, null, 'hike-control-point-end');
      return;
    }

    const [firstKey, firstRoute] = mains[0];
    const [lastKey, lastRoute] = mains[mains.length - 1];

    // When the trail finish is attached to a destination, the item's icon
    // wrapped in the stage ring replaces the finish pin.
    const finishAttached = Object.values(
      getters.attachedStageDestinations as { [dayNumber: number]: AttachedStageDestination }
    ).some(entry => entry.routeKey === lastKey);

    const startCoords = (firstKey === state.activeRouteKey || attachedTrailStart)
      ? null
      : firstRoute.markers[0].markerGeometry.coordinates;
    const endCoords = (lastKey === state.activeRouteKey || finishAttached)
      ? null
      : lastRoute.markers[lastRoute.markers.length - 1].markerGeometry.coordinates;

    upsert(TRAIL_START_SOURCE, TRAIL_START_LAYER, startCoords, 'hike-control-point-start');
    upsert(TRAIL_END_SOURCE, TRAIL_END_LAYER, endCoords, 'hike-control-point-end');
  },

  /**
   * "Start at the previous route's end": while the active route is empty,
   * render a hoverable/clickable anchor on the end point of the closest
   * preceding route. Clicking it drops the new route's first marker exactly
   * on that endpoint (which then reads as a coincident stage connector).
   */
  async updateStartAnchorOnMap({ state, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    const cleanupAnchor = () => {
      anchorListenerRefs.forEach(ref => {
        map.off('mouseenter', START_ANCHOR_LAYER, ref as any);
        map.off('mousemove', START_ANCHOR_LAYER, ref as any);
        map.off('mouseleave', START_ANCHOR_LAYER, ref as any);
        map.off('click', START_ANCHOR_LAYER, ref as any);
      });
      anchorListenerRefs = [];
      safeRemoveLayer(START_ANCHOR_LAYER);
      if (map.getSource(START_ANCHOR_SOURCE)) {
        try { map.removeSource(START_ANCHOR_SOURCE); } catch (_) {}
      }
    };

    // Only offer the anchor while the active route has nothing drawn yet
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (!activeRoute || activeRoute.markers.length > 0) {
      cleanupAnchor();
      return;
    }

    // Closest preceding route (by position) that has an endpoint to offer
    const previousEntry = Object.entries(state.routes)
      .filter(([key, route]) => key !== state.activeRouteKey &&
        route.markers.length > 0 &&
        route.position < activeRoute.position)
      .sort((a, b) => b[1].position - a[1].position)[0];
    if (!previousEntry) {
      cleanupAnchor();
      return;
    }

    const prevRoute = previousEntry[1];
    const endCoords = prevRoute.markers[prevRoute.markers.length - 1].markerGeometry.coordinates;

    const fc: FeatureCollection = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [endCoords[0], endCoords[1]] },
        properties: { lng: endCoords[0], lat: endCoords[1] }
      }]
    };

    const existing = map.getSource(START_ANCHOR_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (existing && map.getLayer(START_ANCHOR_LAYER)) {
      try { existing.setData(fc as any); } catch (_) {}
      if (anchorListenerRefs.length > 0) return;
    } else {
      layerRegistry.addSource(START_ANCHOR_SOURCE, { type: 'geojson', data: fc }, true);
      // Invisible hit area — nothing renders until the user hovers the
      // endpoint, and then only the cursor changes. Opacity 0.01 (not 0)
      // keeps the layer part of hit-testing, same trick as the hover layers.
      const anchorLayer: mapboxgl.CircleLayer = {
        id: START_ANCHOR_LAYER,
        type: 'circle',
        source: START_ANCHOR_SOURCE,
        paint: {
          'circle-radius': 12,
          'circle-color': '#000000',
          'circle-opacity': 0.01,
          'circle-stroke-width': 0,
          'circle-pitch-alignment': 'map'
        }
      };
      layerRegistry.addLayer(anchorLayer, MapLayerCategory.Annotations, { z: 16 });
    }

    if (!map.getLayer(START_ANCHOR_LAYER)) return;

    // Cursor on the canvas ELEMENT and re-asserted per-move — the global
    // hover handler resets 'crosshair' there on every mousemove.
    const canvas = map.getCanvas();

    // 'copy' = pointer with the green plus badge (same cursor hike-planner
    // uses for its snap targets) — "click to start the route here"
    function anchorHover() {
      canvas.style.cursor = 'copy';
    }

    function anchorLeave() {
      canvas.style.cursor = 'crosshair';
    }

    async function anchorClick(e: mapboxgl.MapMouseEvent & { features?: any[] }) {
      const feature = e.features && e.features[0];
      if (!feature) return;
      canvas.style.cursor = 'crosshair';
      // Start the new route exactly on the previous route's end point.
      // mapClicked ignores clicks on this layer, so no duplicate marker.
      await (dispatch as any)('addMarkerAtCoordinates', {
        lng: feature.properties.lng,
        lat: feature.properties.lat
      });
    }

    anchorListenerRefs = [anchorHover, anchorLeave, anchorClick];
    map.on('mouseenter', START_ANCHOR_LAYER, anchorHover);
    map.on('mousemove', START_ANCHOR_LAYER, anchorHover);
    map.on('mouseleave', START_ANCHOR_LAYER, anchorLeave);
    map.on('click', START_ANCHOR_LAYER, anchorClick as any);
  },

  async removeStageConnectorsFromMap({ }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;
    cleanupConnectorListeners(map);
    removeConnectorLayers(map);
    handleData = null;

    // "Start here" anchor
    anchorListenerRefs.forEach(ref => {
      map.off('mouseenter', START_ANCHOR_LAYER, ref as any);
      map.off('mousemove', START_ANCHOR_LAYER, ref as any);
      map.off('mouseleave', START_ANCHOR_LAYER, ref as any);
      map.off('click', START_ANCHOR_LAYER, ref as any);
    });
    anchorListenerRefs = [];
    safeRemoveLayer(START_ANCHOR_LAYER);
    if (map.getSource(START_ANCHOR_SOURCE)) {
      try { map.removeSource(START_ANCHOR_SOURCE); } catch (_) {}
    }

    // Trail endpoint pins live alongside the connectors — remove them too
    safeRemoveLayer(TRAIL_START_LAYER);
    safeRemoveLayer(TRAIL_END_LAYER);
    safeRemoveLayer(TRAIL_START_RING_ICON_LAYER);
    safeRemoveLayer(TRAIL_START_RING_LAYER);
    [TRAIL_START_SOURCE, TRAIL_END_SOURCE, TRAIL_START_RING_SOURCE].forEach(sourceId => {
      if (map.getSource(sourceId)) {
        try { map.removeSource(sourceId); } catch (_) {}
      }
    });
  }
};
